import { database, isFirebaseReady } from './config';
import { ref, push, get, onValue, off, query, orderByChild, set, remove } from 'firebase/database';
import { processActForElo, consolidatePlayerEloHistory } from './eloService';

// Test Firebase connection
export const testFirebaseConnection = async () => {
  try {
    console.log('🔥 Testing Firebase connection...');
    console.log('🔥 isFirebaseReady:', isFirebaseReady);
    console.log('🔥 database object:', database);
    
    if (!isFirebaseReady || !database) {
      console.error('❌ Database not initialized - isFirebaseReady:', isFirebaseReady, 'database:', !!database);
      return false;
    }
    
    const testRef = ref(database, 'connection-test');
    console.log('🔥 Writing test data to Firebase...');
    
    // Use set instead of push for simpler testing
    await set(testRef, { 
      test: true, 
      timestamp: Date.now(),
      message: 'Firebase connection test successful'
    });
    
    console.log('✅ Firebase connection successful!');
    return true;
  } catch (error) {
    console.error('❌ Firebase connection failed:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    
    if (error.code === 'PERMISSION_DENIED') {
      console.error('❌ Permission denied - check Firebase rules');
    }
    
    return false;
  }
};

const HISTORY_PATH = 'act-history';
const DRAFTS_PATH = 'act-drafts';

// ACT History Functions
export const saveActToHistory = async (actData) => {
  try {
    console.log('🔥 Attempting to save ACT to Firebase:', actData);
    
    if (!isFirebaseReady || !database) {
      throw new Error('Firebase not ready - cannot save ACT. Please check Firebase connection.');
    }
    
    const historyRef = ref(database, HISTORY_PATH);
    
    // Add timestamp for ordering
    const actWithTimestamp = {
      ...actData,
      timestamp: Date.now()
    };
    
    console.log('🔥 Pushing to Firebase with data:', actWithTimestamp);
    const newActRef = await push(historyRef, actWithTimestamp);
    
    const savedAct = {
      ...actWithTimestamp,
      id: newActRef.key
    };
    
    console.log('✅ Successfully saved ACT to Firebase with ID:', newActRef.key);
    
    // Update Elo ratings for all races in this ACT
    try {
      await processActForElo(actWithTimestamp);
      console.log('✅ Elo ratings updated for ACT');
    } catch (eloError) {
      console.error('❌ Error updating Elo ratings:', eloError);
      // Don't throw - we still want to save the ACT even if Elo update fails
    }
    
    return savedAct;
  } catch (error) {
    console.error('❌ Error saving ACT to Firebase:', error);
    throw error;
  }
};

export const getActHistory = async () => {
  try {
    const historyRef = ref(database, HISTORY_PATH);
    const snapshot = await get(historyRef);
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      // Convert Firebase object to array and sort by timestamp (newest first)
      const historyArray = Object.values(data).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      return historyArray;
    }
    return [];
  } catch (error) {
    console.error('Error fetching ACT history from Firebase:', error);
    throw error;
  }
};

export const subscribeToActHistory = (callback) => {
  console.log('🔥 Setting up Firebase subscription for ACT history');
  
  // Check if Firebase is ready and database is initialized
  if (!isFirebaseReady || !database) {
    console.error('❌ Firebase not ready, cannot load history');
    callback([]); // Return empty array instead of localStorage data
    return () => {}; // Return empty unsubscribe function
  }
  
  const historyRef = ref(database, HISTORY_PATH);
  const historyQuery = query(historyRef, orderByChild('timestamp'));
  
  const unsubscribe = onValue(historyQuery, (snapshot) => {
    console.log('🔥 Firebase subscription triggered');
    if (snapshot.exists()) {
      const data = snapshot.val();
      console.log('📊 Raw Firebase data:', data);
      // Convert Firebase object to array, ensuring each entry has the Firebase key as its ID
      const historyArray = Object.entries(data).map(([firebaseKey, entry]) => ({
        ...entry,
        id: firebaseKey  // Use Firebase key as the ID for proper deletion
      })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      console.log('📊 Processed history array:', historyArray.length, 'entries');
      callback(historyArray);
    } else {
      console.log('📊 No data found in Firebase');
      callback([]);
    }
  }, (error) => {
    console.error('❌ Error in history subscription:', error);
    callback([]);
  });

  // Return unsubscribe function
  return () => {
    console.log('🔥 Unsubscribing from Firebase');
    off(historyRef, 'value', unsubscribe);
  };
};

// Update ACT date
export const updateActDate = async (actId, newDate) => {
  try {
    console.log('📅 Attempting to update ACT date with ID:', actId, 'to:', newDate);
    
    if (!isFirebaseReady || !database) {
      throw new Error('Firebase not ready - cannot update ACT date. Please check Firebase connection.');
    }
    
    // Validate the date
    const dateObj = new Date(newDate);
    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date provided');
    }
    
    // First, check if the entry exists
    const entryRef = ref(database, `${HISTORY_PATH}/${actId}`);
    const snapshot = await get(entryRef);
    
    if (!snapshot.exists()) {
      console.error('❌ Entry not found in Firebase with ID:', actId);
      throw new Error(`ACT entry not found in database with ID: ${actId}`);
    }
    
    console.log('📊 Found entry, updating date...');
    
    // Since actId is the Firebase key (set in subscription), use it directly
    const dateRef = ref(database, `${HISTORY_PATH}/${actId}/date`);
    await set(dateRef, dateObj.toISOString());
    
    console.log('✅ Successfully updated ACT date in Firebase');
    return true;
  } catch (error) {
    console.error('❌ Error updating ACT date in Firebase:', error);
    throw error;
  }
};

// Update player name in ACT entry
export const updatePlayerName = async (actId, team, player, newName) => {
  try {
    console.log('✏️ Attempting to update player name:', { actId, team, player, newName });

    if (!isFirebaseReady || !database) {
      throw new Error('Firebase not ready - cannot update player name. Please check Firebase connection.');
    }

    // First, check if the entry exists
    const entryRef = ref(database, `${HISTORY_PATH}/${actId}`);
    const snapshot = await get(entryRef);

    if (!snapshot.exists()) {
      console.error('❌ Entry not found in Firebase with ID:', actId);
      throw new Error(`ACT entry not found in database with ID: ${actId}`);
    }

    const entryData = snapshot.val();
    console.log('📊 Found entry data:', entryData);

    // Get the old player name before updating
    const oldName = entryData.teams?.[team]?.players?.[player] || `Player ${player}`;
    console.log(`🔄 Updating player from "${oldName}" to "${newName}"`);

    // Ensure teams structure exists with error handling
    try {
      if (!entryData.teams) {
        console.log('⚠️ Teams structure missing, creating it...');
        await set(ref(database, `${HISTORY_PATH}/${actId}/teams`), {});
        entryData.teams = {}; // Update local copy
      }

      if (!entryData.teams[team]) {
        console.log(`⚠️ Team ${team} structure missing, creating it...`);
        await set(ref(database, `${HISTORY_PATH}/${actId}/teams/${team}`), { players: {} });
        entryData.teams[team] = { players: {} }; // Update local copy
      }

      if (!entryData.teams[team]?.players) {
        console.log(`⚠️ Players structure missing for team ${team}, creating it...`);
        await set(ref(database, `${HISTORY_PATH}/${actId}/teams/${team}/players`), {});
        entryData.teams[team].players = {}; // Update local copy
      }
    } catch (structureError) {
      console.error('❌ Failed to create team structure:', structureError);
      throw new Error(`Failed to create team structure: ${structureError.message}`);
    }

    // Update the player name in team roster with error handling
    try {
      const playerRef = ref(database, `${HISTORY_PATH}/${actId}/teams/${team}/players/${player}`);
      await set(playerRef, newName);
      console.log(`✅ Successfully updated player ${player} in team ${team} to "${newName}"`);
    } catch (error) {
      console.error(`❌ Failed to update player name in team roster:`, error);
      throw new Error(`Failed to update player name in team roster: ${error.message}`);
    }

    // Now update all race data where this player appears as racer
    let raceUpdatesCount = 0;
    let raceUpdateErrors = [];

    if (entryData.races && oldName !== newName) {
      console.log(`🏁 Scanning race data to update "${oldName}" to "${newName}"...`);

      for (const cup of Object.keys(entryData.races)) {
        for (const raceNum of Object.keys(entryData.races[cup])) {
          for (const raceTeam of Object.keys(entryData.races[cup][raceNum])) {
            const raceData = entryData.races[cup][raceNum][raceTeam];
            if (raceData && raceData.racer === oldName) {
              console.log(`  🔄 Updating race ${cup}-${raceNum}-${raceTeam}: "${oldName}" -> "${newName}"`);

              try {
                // Update the racer name in Firebase with error handling
                const raceRef = ref(database, `${HISTORY_PATH}/${actId}/races/${cup}/${raceNum}/${raceTeam}/racer`);
                await set(raceRef, newName);
                raceUpdatesCount++;
                console.log(`    ✅ Race update successful`);
              } catch (raceError) {
                const errorMsg = `Failed to update race ${cup}-${raceNum}-${raceTeam}: ${raceError.message}`;
                console.error(`    ❌ ${errorMsg}`);
                raceUpdateErrors.push(errorMsg);
              }
            }
          }
        }
      }

      console.log(`✅ Updated ${raceUpdatesCount} race entries`);

      if (raceUpdateErrors.length > 0) {
        console.warn(`⚠️ ${raceUpdateErrors.length} race updates failed:`, raceUpdateErrors);
        // Continue execution but log the errors - don't fail the entire operation for partial race update failures
      }
    }

    // Also update in localStorage as backup with error handling
    try {
      const localHistory = JSON.parse(localStorage.getItem('ACT_SCORECARD_HISTORY') || '[]');
      const updatedHistory = localHistory.map(item => {
        if (item.id === actId) {
          const updatedItem = { ...item };

          // Update team roster
          if (!updatedItem.teams) updatedItem.teams = {};
          if (!updatedItem.teams[team]) updatedItem.teams[team] = { players: {} };
          if (!updatedItem.teams[team].players) updatedItem.teams[team].players = {};
          updatedItem.teams[team].players[player] = newName;

          // Update race data
          if (updatedItem.races && oldName !== newName) {
            for (const cup of Object.keys(updatedItem.races)) {
              for (const raceNum of Object.keys(updatedItem.races[cup])) {
                for (const raceTeam of Object.keys(updatedItem.races[cup][raceNum])) {
                  const raceData = updatedItem.races[cup][raceNum][raceTeam];
                  if (raceData && raceData.racer === oldName) {
                    updatedItem.races[cup][raceNum][raceTeam].racer = newName;
                  }
                }
              }
            }
          }

          return updatedItem;
        }
        return item;
      });
      localStorage.setItem('ACT_SCORECARD_HISTORY', JSON.stringify(updatedHistory));
      console.log(`✅ Successfully updated localStorage backup`);
    } catch (localError) {
      console.error('❌ Failed to update localStorage backup:', localError);
      // Don't throw - localStorage failure shouldn't prevent Firebase success
    }

    // Consolidate ELO history under new name
    if (oldName !== newName) {
      try {
        console.log(`📊 Attempting to consolidate ELO history...`);
        const eloConsolidated = await consolidatePlayerEloHistory(oldName, newName);
        if (eloConsolidated) {
          console.log(`✅ ELO history consolidated from "${oldName}" to "${newName}"`);
        } else {
          console.log(`📊 No ELO history to consolidate for "${oldName}"`);
        }
      } catch (eloError) {
        console.error('❌ Error consolidating ELO history:', eloError);
        // Don't throw - we still want the name update to succeed even if ELO consolidation fails
      }
    }

    console.log('✅ Successfully updated player name in Firebase and localStorage');
    if (raceUpdatesCount > 0) {
      console.log(`📊 Total updates: Team roster + ${raceUpdatesCount} race entries${raceUpdateErrors.length > 0 ? ` (${raceUpdateErrors.length} race update errors)` : ''}`);
    }

    // Verify the update was successful by re-reading the data
    try {
      const verifyRef = ref(database, `${HISTORY_PATH}/${actId}/teams/${team}/players/${player}`);
      const verifySnapshot = await get(verifyRef);
      const savedName = verifySnapshot.val();

      if (savedName === newName) {
        console.log(`✅ Verification successful: Name is now "${savedName}"`);
        return true;
      } else {
        console.error(`❌ Verification failed: Expected "${newName}", but found "${savedName}"`);
        throw new Error(`Update verification failed: Name did not persist in database`);
      }
    } catch (verifyError) {
      console.error('❌ Failed to verify update:', verifyError);
      throw new Error(`Failed to verify name update: ${verifyError.message}`);
    }
  } catch (error) {
    console.error('❌ Error updating player name in Firebase:', error);
    throw error;
  }
};

// Delete ACT entry
export const deleteActFromHistory = async (firebaseKey) => {
  try {
    console.log('🗑️ Attempting to delete ACT with Firebase key:', firebaseKey);
    
    if (!isFirebaseReady || !database) {
      throw new Error('Firebase not ready - cannot delete ACT. Please check Firebase connection.');
    }
    
    // Use the Firebase key directly to delete the entry
    const entryRef = ref(database, `${HISTORY_PATH}/${firebaseKey}`);
    await remove(entryRef);
    console.log('✅ Successfully deleted ACT from Firebase');
    return true;
  } catch (error) {
    console.error('❌ Error deleting ACT from Firebase:', error);
    throw error;
  }
};

// Draft Functions (for future use)
export const saveDraft = async (draftData, userId = 'default') => {
  try {
    const draftRef = ref(database, `${DRAFTS_PATH}/${userId}`);
    await push(draftRef, draftData);
    return true;
  } catch (error) {
    console.error('Error saving draft to Firebase:', error);
    throw error;
  }
};

export const getDraft = async (userId = 'default') => {
  try {
    const draftRef = ref(database, `${DRAFTS_PATH}/${userId}`);
    const snapshot = await get(draftRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error('Error fetching draft from Firebase:', error);
    throw error;
  }
};

// Migration helper - import existing localStorage data
export const migrateLocalStorageToFirebase = async () => {
  try {
    const localHistory = localStorage.getItem('ACT_SCORECARD_HISTORY');
    if (localHistory) {
      const parsedHistory = JSON.parse(localHistory);
      
      // Save each entry to Firebase
      const promises = parsedHistory.map(entry => {
        const entryWithTimestamp = {
          ...entry,
          timestamp: new Date(entry.date).getTime()
        };
        return saveActToHistory(entryWithTimestamp);
      });
      
      await Promise.all(promises);
      console.log('Successfully migrated', parsedHistory.length, 'entries to Firebase');
      
      // Optionally clear localStorage after successful migration
      // localStorage.removeItem('ACT_SCORECARD_HISTORY');
      
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error migrating data to Firebase:', error);
    throw error;
  }
};